import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import './utils/responsiveTest'; // Import responsive testing utility
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Subcategories from './pages/Subcategories';
import Companies from './pages/Companies';
import SpecialOffers from './pages/SpecialOffers';
import FutureProducts from './pages/FutureProducts';
import ProductGroups from './pages/ProductGroups';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import CustomerSubscriptions from './pages/CustomerSubscriptions';
import Employees from './pages/Employees';
import Subscriptions from './pages/Subscriptions';
import SubscriptionsEnhanced from './pages/SubscriptionsEnhanced';
import SystemAdmin from './pages/SystemAdmin';
import DashboardEnhanced from './pages/DashboardEnhanced';
import TodaysOrders from './pages/TodaysOrders';
import OrderAnalytics from './pages/OrderAnalytics';
import DeliveryBoyModule from './pages/DeliveryBoyModule';
import DeliveryManagement from './pages/DeliveryManagement';
import DeliveryManagementNew from './pages/DeliveryManagementNew';
import BottleTracking from './pages/BottleTracking';
import SchedulerManagement from './pages/SchedulerManagement';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Permissions from './pages/Permissions';
import NotFound from './pages/NotFound';
import TestComponent from './components/TestComponent';
import DebugAPI from './components/DebugAPI';
import ResponsiveTestComponent from './components/ResponsiveTestComponent';
import CompaniesDebug from './components/CompaniesDebug';
import EmployeeVerification from './pages/EmployeeVerification';
import ForgotPassword from './pages/ForgotPassword';
import OTPVerification from './components/OTPVerification';
import EmployeePermissions from './pages/EmployeePermissions';
import NoPermissions from './pages/NoPermissions';


// Form Pages (replacing modals)
import CategoryForm from './pages/forms/CategoryForm';
import ProductForm from './pages/forms/ProductForm';
import SubcategoryForm from './pages/forms/SubcategoryForm';
import ProductGroupForm from './pages/forms/ProductGroupForm';
import CustomerForm from './pages/forms/CustomerForm';
import EmployeeForm from './pages/forms/EmployeeForm';
import OrderForm from './pages/forms/OrderForm';
import SubscriptionForm from './pages/forms/SubscriptionForm';
import NotificationForm from './pages/forms/NotificationForm';
import PermissionForm from './pages/forms/PermissionForm';
import SettingsForm from './pages/forms/SettingsForm';
import ChangePasswordForm from './pages/forms/ChangePasswordForm';

// Define route permissions mapping
const routePermissions = [
  { path: '/dashboard', permission: 'dashboard_view' },
  { path: '/todays-orders', permission: 'orders_view' },
  { path: '/order-analytics', permission: 'orders_view' },
  { path: '/delivery-management', permission: 'delivery_boy_access' },
  { path: '/delivery-management-new', permission: 'delivery_boy_access' },
  { path: '/bottle-tracking', permission: 'orders_view' },
  { path: '/delivery-boy', permission: 'delivery_boy_access' },
  { path: '/scheduler', permission: 'scheduler_view' },
  { path: '/products', permission: 'products_view' },
  { path: '/categories', permission: 'categories_view' },
  { path: '/companies', permission: 'companies_view' },
  { path: '/product-groups', permission: 'product_groups_view' },
  { path: '/special-offers', permission: 'products_view' },
  { path: '/future-products', permission: 'products_view' },
  { path: '/orders', permission: 'orders_view' },
  { path: '/customers', permission: 'customers_view' },
  { path: '/customer-subscriptions', permission: 'customers_view' },
  { path: '/employees', permission: 'employees_view' },
  { path: '/subscriptions', permission: 'subscriptions_view' },
  { path: '/notifications', permission: 'notifications_view' },
  { path: '/permissions', permission: 'permissions_view' },
  { path: '/settings', permission: 'settings_view' },
];

const ProtectedRoute = ({ children, requiredPermission = null }) => {
  const { isAuthenticated, loading, hasPermission, employee } = useAuth();

  // Check if user has any permissions at all
  const hasAnyPermission = () => {
    if (!employee || !employee.permissions) return false;
    return employee.permissions.length > 0;
  };

  // Helper function to find first available route
  const getFirstAvailableRoute = () => {
    for (const route of routePermissions) {
      if (hasPermission(route.permission)) {
        return route.path;
      }
    }
    return null; // No permissions found
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    // If user has no permissions at all, show no permissions page
    if (!hasAnyPermission()) {
      return <NoPermissions />;
    }
    
    // Find first available route
    const firstRoute = getFirstAvailableRoute();
    
    // If no route available, show no permissions
    if (!firstRoute) {
      return <NoPermissions />;
    }
    
    return <Navigate to={firstRoute} replace />;
  }

  return children;
};

const DefaultRoute = () => {
  const { hasPermission, employee } = useAuth();

  // Check if user has any permissions at all
  const hasAnyPermission = () => {
    if (!employee || !employee.permissions) return false;
    return employee.permissions.length > 0;
  };

  // Helper function to find first available route
  const getFirstAvailableRoute = () => {
    for (const route of routePermissions) {
      if (hasPermission(route.permission)) {
        return route.path;
      }
    }
    return null; // No permissions found
  };

  // If user has no permissions at all, show no permissions page
  if (!hasAnyPermission()) {
    return <NoPermissions />;
  }

  // Find first available route
  const firstRoute = getFirstAvailableRoute();
  
  // If no route available (shouldn't happen if hasAnyPermission is true), show no permissions
  if (!firstRoute) {
    return <NoPermissions />;
  }

  return <Navigate to={firstRoute} replace />;
};

const App = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-employee/:token" element={<EmployeeVerification />} />
      <Route path="/verify-otp" element={<OTPVerification />} />
      <Route path="/test" element={<TestComponent />} />
      <Route path="/debug-api" element={<DebugAPI />} />
      <Route path="/debug-companies" element={<CompaniesDebug />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredPermission="dashboard_view">
            <Layout>
              <DashboardEnhanced />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/todays-orders"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <TodaysOrders />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/order-analytics"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <OrderAnalytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/delivery-boy"
        element={
          <ProtectedRoute requiredPermission="delivery_boy_access">
            <Layout>
              <DeliveryBoyModule />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/delivery-management"
        element={
          <ProtectedRoute requiredPermission="delivery_boy_access">
            <Layout>
              <DeliveryManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/delivery-management-new"
        element={
          <ProtectedRoute requiredPermission="delivery_boy_access">
            <Layout>
              <DeliveryManagementNew />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/bottle-tracking"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <BottleTracking />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/scheduler"
        element={
          <ProtectedRoute requiredPermission="scheduler_view">
            <Layout>
              <SchedulerManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/products"
        element={
          <ProtectedRoute requiredPermission="products_view">
            <Layout>
              <Products />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/categories"
        element={
          <ProtectedRoute requiredPermission="categories_view">
            <Layout>
              <Categories />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/companies"
        element={
          <ProtectedRoute requiredPermission="companies_view">
            <Layout>
              <Companies />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/special-offers"
        element={
          <ProtectedRoute requiredPermission="products_view">
            <Layout>
              <SpecialOffers />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/future-products"
        element={
          <ProtectedRoute requiredPermission="products_view">
            <Layout>
              <FutureProducts />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/product-groups"
        element={
          <ProtectedRoute requiredPermission="product_groups_view">
            <Layout>
              <ProductGroups />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/customers"
        element={
          <ProtectedRoute requiredPermission="customers_view">
            <Layout>
              <Customers />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/customer-subscriptions"
        element={
          <ProtectedRoute requiredPermission="customers_view">
            <Layout>
              <CustomerSubscriptions />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/employees"
        element={
          <ProtectedRoute requiredPermission="employees_view">
            <Layout>
              <Employees />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/employees/:employeeId/permissions"
        element={
          <ProtectedRoute requiredPermission="employees_view">
            <Layout>
              <EmployeePermissions />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute requiredPermission="subscriptions_view">
            <Layout>
              <SubscriptionsEnhanced />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/system-admin"
        element={
          <ProtectedRoute requiredPermission="super_admin_access">
            <Layout>
              <SystemAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/notifications"
        element={
          <ProtectedRoute requiredPermission="notifications_view">
            <Layout>
              <Notifications />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredPermission="settings_view">
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/permissions"
        element={
          <ProtectedRoute requiredPermission="permissions_view">
            <Layout>
              <Permissions />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Form Routes (replacing modals) */}
      <Route
        path="/categories/new"
        element={
          <ProtectedRoute requiredPermission="categories_view">
            <Layout>
              <CategoryForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories/:id/edit"
        element={
          <ProtectedRoute requiredPermission="categories_view">
            <Layout>
              <CategoryForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/products/new"
        element={
          <ProtectedRoute requiredPermission="products_view">
            <Layout>
              <ProductForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute requiredPermission="products_view">
            <Layout>
              <ProductForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/subcategories/new"
        element={
          <ProtectedRoute requiredPermission="subcategories_view">
            <Layout>
              <SubcategoryForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcategories/:id/edit"
        element={
          <ProtectedRoute requiredPermission="subcategories_view">
            <Layout>
              <SubcategoryForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/product-groups/new"
        element={
          <ProtectedRoute requiredPermission="product_groups_view">
            <Layout>
              <ProductGroupForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-groups/:uuid/edit"
        element={
          <ProtectedRoute requiredPermission="product_groups_view">
            <Layout>
              <ProductGroupForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/customers/new"
        element={
          <ProtectedRoute requiredPermission="customers_view">
            <Layout>
              <CustomerForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id/edit"
        element={
          <ProtectedRoute requiredPermission="customers_view">
            <Layout>
              <CustomerForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/employees/new"
        element={
          <ProtectedRoute requiredPermission="employees_view">
            <Layout>
              <EmployeeForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id/edit"
        element={
          <ProtectedRoute requiredPermission="employees_view">
            <Layout>
              <EmployeeForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders/new"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <OrderForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id/edit"
        element={
          <ProtectedRoute requiredPermission="orders_view">
            <Layout>
              <OrderForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/subscriptions/new"
        element={
          <ProtectedRoute requiredPermission="subscriptions_view">
            <Layout>
              <SubscriptionForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/:id/edit"
        element={
          <ProtectedRoute requiredPermission="subscriptions_view">
            <Layout>
              <SubscriptionForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/notifications/new"
        element={
          <ProtectedRoute requiredPermission="notifications_view">
            <Layout>
              <NotificationForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/:id/edit"
        element={
          <ProtectedRoute requiredPermission="notifications_view">
            <Layout>
              <NotificationForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/permissions/new"
        element={
          <ProtectedRoute requiredPermission="permissions_view">
            <Layout>
              <PermissionForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/permissions/:id/edit"
        element={
          <ProtectedRoute requiredPermission="permissions_view">
            <Layout>
              <PermissionForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings/new"
        element={
          <ProtectedRoute requiredPermission="settings_view">
            <Layout>
              <SettingsForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings/:id/edit"
        element={
          <ProtectedRoute requiredPermission="settings_edit">
            <Layout>
              <SettingsForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile/change-password"
        element={
          <ProtectedRoute>
            <Layout>
              <ChangePasswordForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<DefaultRoute />} />
      <Route path="/no-permissions" element={<NoPermissions />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App; 